import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/client';
import type { SiteReport, Project } from '../types';

const typeLabels: Record<string, string> = {
  daily: 'Tagesbericht',
  acceptance: 'Abnahmeprotokoll',
  regie: 'Regiebericht',
};
const typeStyles: Record<string, string> = {
  daily: 'bg-blue-50 text-blue-700 border border-blue-200',
  acceptance: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  regie: 'bg-amber-50 text-amber-700 border border-amber-200',
};

const weatherOptions = ['Sonnig', 'Bewölkt', 'Regen', 'Schnee', 'Sturm', 'Nebel'];

export default function ReportsPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Partial<SiteReport> | null>(null);

  const { data: reports = [], isLoading } = useQuery<SiteReport[]>({
    queryKey: ['reports'],
    queryFn: () => api.get('/reports').then((r) => r.data),
  });

  const { data: projects = [] } = useQuery<Project[]>({
    queryKey: ['projects'],
    queryFn: () => api.get('/projects').then((r) => r.data),
  });

  const saveMutation = useMutation({
    mutationFn: (r: Partial<SiteReport>) =>
      r.id ? api.put(`/reports/${r.id}`, r) : api.post('/reports', r),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['reports'] }); setEditing(null); },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/reports/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reports'] }),
  });

  const fmtDate = (s: string) =>
    new Date(s).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

  const newReport = () => {
    setEditing({
      projectId: '',
      type: 'daily',
      title: '',
      reportDate: new Date().toISOString().slice(0, 10),
      content: { weather: '', temperature: '', workers: 0, workDone: '', materials: '', issues: '', notes: '' },
    });
  };

  const updateContent = (field: string, value: string | number) => {
    if (!editing) return;
    setEditing({ ...editing, content: { ...editing.content, [field]: value } });
  };

  return (
    <div>
      <div className="sp-page-header">
        <div>
          <h1 className="sp-page-title">Bauberichte</h1>
          <p className="sp-page-subtitle">{reports.length} Berichte insgesamt</p>
        </div>
        <button onClick={newReport} className="sp-btn-primary">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Neuer Bericht
        </button>
      </div>

      {editing && (
        <div className="sp-form-card">
          <h3 className="sp-form-title">{editing.id ? 'Bericht bearbeiten' : 'Neuer Baubericht'}</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="sp-label">Titel *</label>
              <input value={editing.title || ''} onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                className="sp-input" placeholder="Tagesbericht 05.04.2026" autoFocus />
            </div>
            <div>
              <label className="sp-label">Typ *</label>
              <select value={editing.type || 'daily'} onChange={(e) => setEditing({ ...editing, type: e.target.value as SiteReport['type'] })}
                className="sp-input">
                {Object.entries(typeLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="sp-label">Projekt *</label>
              <select value={editing.projectId || ''} onChange={(e) => setEditing({ ...editing, projectId: e.target.value })}
                className="sp-input">
                <option value="">Projekt wählen...</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
              </select>
            </div>
            <div>
              <label className="sp-label">Berichtsdatum *</label>
              <input type="date" value={(editing.reportDate || '').slice(0, 10)}
                onChange={(e) => setEditing({ ...editing, reportDate: e.target.value })}
                className="sp-input" />
            </div>
          </div>

          {/* Wetter & Arbeitskräfte */}
          <div className="mt-5 pt-5 border-t border-surface-100">
            <h4 className="text-[13px] font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
              </svg>
              Bedingungen
            </h4>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="sp-label">Wetter</label>
                <select value={editing.content?.weather || ''} onChange={(e) => updateContent('weather', e.target.value)}
                  className="sp-input">
                  <option value="">Wählen...</option>
                  {weatherOptions.map((w) => <option key={w} value={w}>{w}</option>)}
                </select>
              </div>
              <div>
                <label className="sp-label">Temperatur</label>
                <input value={editing.content?.temperature || ''} onChange={(e) => updateContent('temperature', e.target.value)}
                  className="sp-input" placeholder="z.B. 18 Grad C" />
              </div>
              <div>
                <label className="sp-label">Arbeitskräfte</label>
                <input type="number" value={editing.content?.workers ?? 0}
                  onChange={(e) => updateContent('workers', +e.target.value)}
                  className="sp-input" />
              </div>
            </div>
          </div>

          {/* Arbeitsinhalt */}
          <div className="mt-5 pt-5 border-t border-surface-100">
            <h4 className="text-[13px] font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              Arbeitsinhalt
            </h4>
            <div className="space-y-4">
              <div>
                <label className="sp-label">Ausgeführte Arbeiten *</label>
                <textarea value={editing.content?.workDone || ''}
                  onChange={(e) => updateContent('workDone', e.target.value)}
                  className="sp-input min-h-[80px] resize-y" placeholder="Beschreibung der ausgeführten Arbeiten..." />
              </div>
              <div>
                <label className="sp-label">Verwendete Materialien</label>
                <textarea value={editing.content?.materials || ''}
                  onChange={(e) => updateContent('materials', e.target.value)}
                  className="sp-input min-h-[60px] resize-y" placeholder="Materialien und Mengen..." />
              </div>
              <div>
                <label className="sp-label">Probleme / Behinderungen</label>
                <textarea value={editing.content?.issues || ''}
                  onChange={(e) => updateContent('issues', e.target.value)}
                  className="sp-input min-h-[60px] resize-y" placeholder="Probleme, Verzögerungen, Behinderungen..." />
              </div>
              <div>
                <label className="sp-label">Sonstige Notizen</label>
                <textarea value={editing.content?.notes || ''}
                  onChange={(e) => updateContent('notes', e.target.value)}
                  className="sp-input min-h-[60px] resize-y" placeholder="Weitere Anmerkungen..." />
              </div>
            </div>
          </div>

          <div className="sp-form-actions">
            <button onClick={() => saveMutation.mutate(editing)} disabled={saveMutation.isPending} className="sp-btn-primary">
              {saveMutation.isPending ? 'Speichern...' : 'Speichern'}
            </button>
            <button onClick={() => setEditing(null)} className="sp-btn-secondary">Abbrechen</button>
          </div>
        </div>
      )}

      <div className="sp-card overflow-hidden animate-fade-in">
        <table className="sp-table">
          <thead>
            <tr>
              <th>Datum</th>
              <th>Titel</th>
              <th>Projekt</th>
              <th>Typ</th>
              <th>Ersteller</th>
              <th className="w-28 text-right">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={6}><div className="flex items-center justify-center py-12 gap-2 text-slate-400">
                <svg className="sp-spinner" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" /></svg>
                Laden...</div></td></tr>
            ) : reports.length === 0 ? (
              <tr><td colSpan={6}><div className="sp-empty">
                <div className="w-12 h-12 rounded-2xl bg-surface-100 flex items-center justify-center mb-3">
                  <svg className="w-6 h-6 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                </div>
                <span className="text-sm font-medium">Keine Berichte vorhanden</span>
              </div></td></tr>
            ) : reports.map((r) => (
              <tr key={r.id} onClick={() => setEditing(r)} className="cursor-pointer hover:bg-brand-50/50 transition-colors">
                <td className="font-mono text-xs text-slate-500">{fmtDate(r.reportDate)}</td>
                <td>
                  <div>
                    <span className="font-medium text-slate-800">{r.title}</span>
                    {r.content?.workDone && (
                      <span className="block text-[11px] text-slate-400 line-clamp-1">{r.content.workDone}</span>
                    )}
                  </div>
                </td>
                <td className="text-slate-500">{r.project?.title || '-'}</td>
                <td>
                  <span className={`sp-badge ${typeStyles[r.type] || ''}`}>
                    {typeLabels[r.type] || r.type}
                  </span>
                </td>
                <td className="text-slate-500 text-xs">{r.user?.name || '-'}</td>
                <td className="text-right">
                  <div className="flex items-center justify-end gap-0.5">
                    <button onClick={() => setEditing(r)} className="sp-btn-icon" title="Bearbeiten">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button onClick={() => { if (confirm('Bericht wirklich löschen?')) deleteMutation.mutate(r.id); }}
                      className="sp-btn-icon hover:!text-error-500 hover:!bg-error-50" title="Löschen">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
